import { stripTranslationMarkers } from '@dealstack/frontend/shared/mechanism/i18n/translationMarkers';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { Button, Drawer, Flex, Text, TextInput, rem } from '@mantine/core';
import { ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';
import { GENERIC_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';
import { InfoBox } from '@dealstack/frontend/shared/components/InfoBox/InfoBox.tsx';

interface Props {
	opened: boolean;
	onClose: () => void;
	title: string;
	alertTitle?: string;
	hideAlertIcon?: boolean;
	alertDescription?: string;
	promptText?: string;
	confirmDeleteText: string;
	wordMismatchErrorMessage: string;
	inputLabel: string;
	inputPlaceholder: string;
	deleteBtnText: string;
	cancelBtnText: string;
	handleDelete: () => void;
	deleteSuccess?: boolean;
	deleteLoading: boolean;
	tsaConnected?: boolean;
	disableDeleteButton?: boolean;
	additionalInfo?: string;
	tsaNumber?: number;
	enableDeleteButtonOnlyOnConfirmDelete?: boolean;
	children?: ReactNode;
}

export const ConfirmDeleteDrawer = ({
	opened,
	onClose,
	title,
	alertTitle,
	alertDescription,
	promptText,
	hideAlertIcon,
	confirmDeleteText,
	inputLabel,
	inputPlaceholder,
	wordMismatchErrorMessage,
	deleteBtnText,
	cancelBtnText,
	handleDelete,
	deleteSuccess,
	deleteLoading,
	tsaConnected = false,
	disableDeleteButton = false,
	additionalInfo,
	tsaNumber,
	enableDeleteButtonOnlyOnConfirmDelete = false,
	children,
}: Props) => {
	const [confirmTextValue, setConfirmTextValue] = useState<string>('');
	const [wordInputError, setWordInputError] = useState<string>();

	// The word comes from a translation, and a translated phrase is not always the word it looks
	// like: with Tolgee's in-context editor running, every string t() returns carries the key encoded
	// into it, so a phrase reading as "delete" never compares equal to "delete". Both sides are
	// unwrapped, not just the expected one — a user who copies the word off the screen rather than
	// typing it brings the encoding into the input with it, and comparing an unwrapped expectation
	// against a still-wrapped paste rejects the correct word. Trimmed for the same reason.
	const confirmWordMatches =
		stripTranslationMarkers(confirmTextValue).trim() ===
		stripTranslationMarkers(confirmDeleteText).trim();

	useEffect(() => {
		if (deleteSuccess) {
			handleClose();
		}
	}, [deleteSuccess]);

	const handleConfirmDelete = async () => {
		if (!confirmWordMatches) {
			setWordInputError(wordMismatchErrorMessage || 'Words should match');
			return;
		}
		handleDelete();
	};

	const handleInputChange = (value: string) => {
		setConfirmTextValue(value);
		setWordInputError(undefined);
	};

	const handleClose = () => {
		setConfirmTextValue('');
		setWordInputError(undefined);
		onClose();
	};

	return (
		<Drawer
			opened={opened}
			onClose={handleClose}
			position="right"
			transitionProps={drawerTransitionProps}
			trapFocus={false}
			title={
				<span data-testid={GENERIC_ELEMENT_ID.CONFIRM_DELETE_DRAWER_HEADING}>{title}</span>
			}
		>
			<Flex direction="column" justify="space-between" h="100%">
				<Flex direction="column" gap={rem('16px')}>
					{(alertTitle || alertDescription) && (
						<InfoBox
							iconVariant={hideAlertIcon ? undefined : 'warning'}
							rippleColor="red"
							title={alertTitle}
							description={alertDescription}
							matchBorderToRippleColor={true}
							alignContent="center"
						>
							{tsaConnected && additionalInfo && (
								<Text>
									{additionalInfo} {tsaNumber}
								</Text>
							)}
						</InfoBox>
					)}

					{promptText && <Text>{promptText}</Text>}

					{children}

					{!tsaConnected && !disableDeleteButton && (
						<ConfirmationBox>
							<Text
								fz="36px"
								fw={200}
								style={{ lineHeight: 1.3 }}
								data-testid={GENERIC_ELEMENT_ID.CONFIRM_DELETE_DRAWER_CONFIRM_TEXT}
							>
								{confirmDeleteText}
							</Text>
						</ConfirmationBox>
					)}

					{!tsaConnected && !disableDeleteButton && (
						<TextInput
							label={inputLabel}
							placeholder={inputPlaceholder}
							size="md"
							value={confirmTextValue}
							onChange={(event) => handleInputChange(event.currentTarget.value)}
							error={wordInputError}
							data-testid={GENERIC_ELEMENT_ID.CONFIRM_DELETE_DRAWER_INPUT}
						/>
					)}
				</Flex>

				<Flex direction="column" gap={rem('8px')}>
					<Button
						color="red"
						size="md"
						disabled={
							tsaConnected ||
							deleteLoading ||
							disableDeleteButton ||
							(enableDeleteButtonOnlyOnConfirmDelete && !confirmWordMatches)
						}
						onClick={handleConfirmDelete}
						fullWidth={true}
						sx={(theme) => ({
							color: theme.colors.white[0],
							':not(:disabled)': { backgroundColor: theme.colors.red[6] },
						})}
						data-testid={GENERIC_ELEMENT_ID.CONFIRM_DELETE_DRAWER_DELETE_BUTTON}
					>
						{deleteBtnText}
					</Button>
					<Button
						variant="default"
						onClick={handleClose}
						fullWidth={true}
						data-testid={GENERIC_ELEMENT_ID.CONFIRM_DELETE_DRAWER_CANCEL_BUTTON}
					>
						{cancelBtnText}
					</Button>
				</Flex>
			</Flex>
		</Drawer>
	);
};

const ConfirmationBox = styled.div`
	display: flex;
	justify-content: center;
	align-content: center;
	text-align: center;
	background-color: ${({ theme }) => theme.v3.colors.gray[0]};
	border-radius: ${rem('4px')};
	padding: ${rem('16px')};
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
	max-width: 100%;
`;
